/**
 * PrebuiltTemplatesTab — list+tiles of curated prebuilt dashboard templates.
 * iter-121.39 cosmetic redesign: modernised cards with brand-aware icon plate,
 * status chip in the corner, softer hover lift, refined action row, and a
 * cleaner table layout. All data-testids, handlers, and API wiring preserved.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useScopeFilter } from "@/lib/scope-filter-context";
import {
    LayoutDashboard, Trash2, Copy, Database, Sparkles, Layers, Library, Eye, MoreVertical, Lock,
} from "lucide-react";
import ReadinessBadge from "@/components/dashboards/ReadinessBadge";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { IconPlate } from "./MyDashboardsTab";

/* -------------------------------------------------------------------------- */
/*  Status chip — top-right corner pill that quickly signals data readiness   */
/*  for a template. Tone derives from the existing model-binding + demo flags */
/*  so we don't need any new backend fields.                                  */
/* -------------------------------------------------------------------------- */
function StatusChip({ isModelBound, isInstantiated, isDemo }) {
    if (!isModelBound) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/60 border border-border text-[9px] font-mono uppercase tracking-wider text-muted-foreground">
                Template
            </span>
        );
    }
    if (isInstantiated) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 text-[9px] font-mono uppercase tracking-wider text-[hsl(var(--success))]">
                Instantiated
            </span>
        );
    }
    if (isDemo) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-[9px] font-mono uppercase tracking-wider text-rose-700 dark:text-rose-300">
                Sample data
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[9px] font-mono uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            Live ready
        </span>
    );
}

/* -------------------------------------------------------------------------- */
/*  Table view — modernised rows.                                             */
/* -------------------------------------------------------------------------- */
function PrebuiltTemplatesTable({ ctx }) {
    const {
        filteredTemplates, isSuperAdmin, nav, clone,
        delTemplateInstance, hideTemplate, prebuiltLocked, openUpgrade,
    } = ctx;
    return (
        <div className="border border-border rounded-xl bg-card overflow-x-auto shadow-sm" data-testid="templates-list">
            <table className="w-full text-sm min-w-[860px]">
                <thead className="bg-secondary/40">
                    <tr>
                        <th className="text-left px-4 py-2.5 eyebrow">Template</th>
                        <th className="text-left px-4 py-2.5 eyebrow w-28">Category</th>
                        <th className="text-left px-4 py-2.5 eyebrow w-20">Widgets</th>
                        <th className="text-left px-4 py-2.5 eyebrow w-16">Version</th>
                        <th className="text-left px-4 py-2.5 eyebrow w-40">Created by</th>
                        <th className="text-left px-4 py-2.5 eyebrow w-32">Created</th>
                        <th className="w-28" />
                    </tr>
                </thead>
                <tbody>
                    {filteredTemplates.map((t) => (
                        <tr key={t.id} className="border-t border-border hover:bg-secondary/30 cursor-pointer transition-colors" onClick={() => nav(`/dashboards/preview/${t.id}`)} data-testid={`template-row-${t.id}`}>
                            <td className="px-4 py-2.5">
                                <div className="font-medium truncate flex items-center gap-2">
                                    {t.name}
                                    {t.readiness && <ReadinessBadge readiness={t.readiness} compact data-testid={`template-readiness-${t.id}`} />}
                                </div>
                                {t.description && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{t.description}</div>}
                            </td>
                            <td className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{t.category}</td>
                            <td className="px-4 py-2.5 font-mono text-[11px]">{t.widgets?.length || 0}</td>
                            <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground" data-testid={`template-version-${t.id}`}>v{t.version ?? 1}</td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground truncate" title={t.created_by_email || ""} data-testid={`template-created-by-${t.id}`}>{t.created_by_name || t.created_by_email || "—"}</td>
                            <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground" data-testid={`template-created-at-${t.id}`}>{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                            <td className="px-4 py-2.5 text-right">
                                <div className="inline-flex items-center gap-1">
                                    {prebuiltLocked && !t.instance ? (
                                        <Button variant="outline" size="sm" className="rounded-md h-7 text-xs opacity-90"
                                            onClick={(e) => { e.stopPropagation(); openUpgrade?.(); }}
                                            title="Prebuilt dashboards aren't included in your plan."
                                            data-testid={`template-clone-${t.id}-locked`}>
                                            <Lock className="h-3 w-3 mr-1" /> Upgrade
                                        </Button>
                                    ) : (
                                        <Button variant="outline" size="sm" className="rounded-md h-7 text-xs" onClick={(e) => { e.stopPropagation(); clone(t); }} data-testid={`template-clone-${t.id}`}>
                                            <Copy className="h-3 w-3 mr-1" /> {t.instance ? "Open" : "Clone"}
                                        </Button>
                                    )}
                                    {t._model_bound && t.instance?.id && (
                                        <Button variant="ghost" size="sm" className="rounded-md h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                                            onClick={(e) => { e.stopPropagation(); delTemplateInstance(t); }}
                                            title="Delete instantiated dashboard"
                                            data-testid={`template-delete-${t.id}`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                    {isSuperAdmin && t.slug && (
                                        <Button variant="ghost" size="sm" className="rounded-md h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                                            onClick={(e) => { e.stopPropagation(); hideTemplate(t); }}
                                            title="Permanently delete this prebuilt template (super-admin only)"
                                            data-testid={`template-hide-${t.slug}`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Tile view — premium template card.                                        */
/* -------------------------------------------------------------------------- */
function PrebuiltTemplatesTiles({ ctx }) {
    const {
        filteredTemplates, isSuperAdmin, nav, clone,
        delTemplateInstance, hideTemplate, savePlaybookFromTemplate,
        setTileSourceTarget, load,
    } = ctx;
    const { tenantId: scopeTenantId, orgId: scopeOrgId } = useScopeFilter();

    const onConnectData = async (t) => {
        let target = t.instance;
        if (!target?.id) {
            try {
                const params = {};
                if (scopeTenantId) params.tenant_id = scopeTenantId;
                if (scopeOrgId) params.org_id = scopeOrgId;
                const r = await api.post(`/dashboards/from-template/${t.id}`, null, { params });
                target = r.data;
                load();
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not start template");
                return;
            }
        }
        setTileSourceTarget({ id: target.id, name: target.name || t.name });
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTemplates.map((t) => {
                const isModelBound = t._model_bound;
                const isInstantiated = !!t.instance;
                const isDemo = isModelBound && t.demo_mode;
                const sampleData = isDemo;
                return (
                    <div key={t.id}
                         className="group relative border border-border bg-card rounded-xl p-5 cursor-pointer shadow-sm hover:shadow-md hover:border-foreground/15 hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
                         onClick={() => nav(`/dashboards/preview/${t.id}`)}
                         title="Click to preview this template with sample data"
                         data-testid={`template-card-${t.id}`}>
                        {/* Top row: SAMPLE DATA or status chip (left) + DB icon (right) */}
                        <div className="flex items-start justify-between mb-3.5 gap-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                {sampleData ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-[9px] font-mono uppercase tracking-wider text-rose-700 dark:text-rose-300">
                                        <Database className="h-2.5 w-2.5" /> Sample data
                                    </span>
                                ) : (
                                    <StatusChip isModelBound={isModelBound} isInstantiated={isInstantiated} isDemo={isDemo} />
                                )}
                                {t.is_unified && (
                                    <Badge variant="outline" className="font-mono text-[9px] border-primary/30 bg-primary/10 text-primary gap-0.5" data-testid={`template-unified-${t.id}`}>
                                        <Layers className="h-2.5 w-2.5" /> UNIFIED
                                    </Badge>
                                )}
                                {t.created_via === "sense_ai_builder" && (
                                    <Badge variant="outline"
                                           className="font-mono text-[9px] border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 gap-0.5"
                                           title={t.builder_prompt
                                               ? `Generated from prompt: "${t.builder_prompt}"`
                                               : "Generated by the Sense AI Builder"}
                                           data-testid={`template-builder-badge-${t.id}`}>
                                        <Sparkles className="h-2.5 w-2.5" /> SENSE AI
                                    </Badge>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5">
                                {t.readiness && <ReadinessBadge readiness={t.readiness} compact data-testid={`template-readiness-${t.id}`} />}
                                <div className="h-7 w-7 rounded-md border border-border bg-secondary/40 flex items-center justify-center text-muted-foreground" title={isModelBound ? "Model-bound template" : "Custom template"}>
                                    <Database className="h-3.5 w-3.5" />
                                </div>
                            </div>
                        </div>

                        {/* Icon plate */}
                        <div className="mb-3">
                            <IconPlate name={t.name} category={t.category} />
                        </div>

                        {/* Title + description */}
                        <div className="mb-3 flex-1">
                            <div className="text-[15px] font-semibold leading-snug text-foreground line-clamp-2">{t.name}</div>
                            <div className="text-xs text-muted-foreground mt-1 line-clamp-3">{t.description}</div>
                        </div>

                        {/* Model-binding badges */}
                        {isModelBound && (t.bind_to_models || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3.5" data-testid={`template-bindings-${t.id}`}>
                                {(t.bind_to_models || []).map((m) => (
                                    <Badge key={m} variant="outline" className="font-mono text-[9px] border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 gap-0.5">
                                        <span className="opacity-60">model.</span>{m}
                                        {(t.bound_counts || {})[m] != null && (
                                            <span className="opacity-60">· {t.bound_counts[m].toLocaleString()} rows</span>
                                        )}
                                    </Badge>
                                ))}
                            </div>
                        )}

                        {/* Footer: widgets count + action buttons + more menu */}
                        <div className="pt-3 border-t border-border/60 flex items-center justify-between gap-1.5">
                            <div className="flex flex-col">
                                <span className="font-mono text-base font-semibold text-foreground leading-none">{t.widgets?.length || 0}</span>
                                <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">Widgets</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" className="rounded-md h-8 px-2.5 text-xs gap-1 text-muted-foreground hover:text-foreground"
                                    onClick={(e) => { e.stopPropagation(); nav(`/dashboards/preview/${t.id}`); }}
                                    title="Preview this template with sample data"
                                    data-testid={`template-preview-${t.id}`}>
                                    <Eye className="h-3.5 w-3.5" /> Preview
                                </Button>
                                <Button variant="default" size="sm" className="rounded-md h-8 px-3 text-xs shadow-sm"
                                    onClick={(e) => { e.stopPropagation(); clone(t); }}
                                    data-testid={`template-clone-${t.id}`}>
                                    {isInstantiated ? "Open" : "Use"}
                                </Button>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" className="rounded-md h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`template-more-${t.id}`}
                                            title="More actions">
                                            <MoreVertical className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
                                        {(isModelBound || isInstantiated) && (
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onConnectData(t); }}
                                                              data-testid={`template-connect-data-${t.id}`}>
                                                <Database className="h-3.5 w-3.5 mr-2" /> Connect data
                                            </DropdownMenuItem>
                                        )}
                                        {isSuperAdmin && (
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); savePlaybookFromTemplate(t); }}
                                                              data-testid={`template-save-playbook-${t.id}`}>
                                                <Library className="h-3.5 w-3.5 mr-2" /> Save as playbook
                                            </DropdownMenuItem>
                                        )}
                                        {(isModelBound && isInstantiated) || (isSuperAdmin && t.slug)
                                            ? <DropdownMenuSeparator /> : null}
                                        {isModelBound && isInstantiated && (
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); delTemplateInstance(t); }}
                                                              className="text-destructive focus:text-destructive"
                                                              data-testid={`template-delete-${t.id}`}>
                                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete instance
                                            </DropdownMenuItem>
                                        )}
                                        {isSuperAdmin && t.slug && (
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); hideTemplate(t); }}
                                                              className="text-destructive focus:text-destructive"
                                                              data-testid={`template-hide-${t.slug}`}>
                                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete template
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function PrebuiltTemplatesTab({ ctx }) {
    const { viewMode, filteredTemplates } = ctx;
    if (filteredTemplates.length === 0) {
        return (
            <div className="text-center py-16 text-muted-foreground text-sm border border-dashed border-border rounded-xl bg-secondary/20" data-testid="templates-empty">
                <LayoutDashboard className="h-10 w-10 mx-auto mb-3 opacity-30" />
                No templates match your filter.
            </div>
        );
    }
    return viewMode === "details"
        ? <PrebuiltTemplatesTable ctx={ctx} />
        : <PrebuiltTemplatesTiles ctx={ctx} />;
}
